"use client"

import { useEffect } from "react"
import posthog from "posthog-js"

// Root error boundary. Per-segment error.tsx files cannot catch crashes that
// originate in the root layout or its providers (PostHogProvider, theme, etc.)
// because those boundaries live *inside* that tree. global-error replaces the
// whole document when that happens, so it must render its own <html>/<body>
// and stay dependency-light — none of the app providers are mounted here.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Capture via the posthog-js singleton directly: the React provider/hook
    // is part of the tree that just crashed, so it isn't available here.
    posthog.capture("react_error_boundary_triggered", {
      boundary: "global",
      error_name: error.name || "Error",
      error_message: error.message?.slice(0, 300),
      error_stack: error.stack?.slice(0, 1000),
      digest: error.digest,
    })
  }, [error])

  return (
    <html lang="en">
      <body>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.75rem",
            padding: "1.5rem",
            textAlign: "center",
            fontFamily:
              "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          }}
        >
          <h1 style={{ fontSize: "1.125rem", fontWeight: 600 }}>
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#6b7280",
              maxWidth: "28rem",
            }}
          >
            Try refreshing the page. If the problem keeps happening, contact
            support and include the code below.
          </p>
          {error.digest && (
            <code
              style={{
                borderRadius: "0.25rem",
                background: "#f3f4f6",
                padding: "0.25rem 0.5rem",
                fontSize: "0.75rem",
              }}
            >
              {error.digest}
            </code>
          )}
          <button
            type="button"
            onClick={reset}
            style={{
              marginTop: "0.5rem",
              borderRadius: "0.375rem",
              background: "#111827",
              color: "#ffffff",
              padding: "0.5rem 1rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              border: "none",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  )
}
